import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import MovdLogo from '../components/MovdLogo.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const navItems = [
  { label: 'Current move', path: '/customer-portal', icon: '\u2302', end: true },
  { label: 'Bookings', path: '/customer-portal/bookings', icon: '\uD83D\uDCC5' },
  { label: 'Messages', path: '/customer-portal/messages', icon: '\uD83D\uDCAC' },
  { label: 'Account settings', path: '/customer-portal/settings', icon: '\u2699' },
];

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function CustomerPortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  let user = {};
  try { user = JSON.parse(localStorage.getItem('userData') || '{}'); } catch { /* Keep the portal shell available. */ }

  const signOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    window.dispatchEvent(new Event('movd-auth-changed'));
    navigate('/', { replace: true });
  };

  const sidebar = (
    <div style={{ width: isMobile ? 260 : 248, height: '100%', background: movdTokens.color.brand.ink, color: movdTokens.color.text, display: 'flex', flexDirection: 'column', padding: 16, boxSizing: 'border-box' }}>
      <div style={{ padding: '0 4px 24px' }}><MovdLogo size="small" variant="full" color="white" /></div>
      <p style={{ color: '#93C5FD', fontWeight: 800, letterSpacing: '.1em', fontSize: 11, padding: '0 4px', margin: '0 0 8px', textTransform: 'uppercase' }}>Customer portal</p>
      <nav style={{ display: 'grid', gap: 4 }}>
        {navItems.map(({ label, path, icon, end }) => (
          <NavLink
            key={path}
            to={path}
            end={end}
            onClick={() => setMobileOpen(false)}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10, color: isActive ? '#fff' : 'rgba(255,255,255,.72)', background: isActive ? movdTokens.color.action.primary : 'transparent',
              textDecoration: 'none', fontWeight: 700, padding: '10px 12px', borderRadius: 8,
            })}
          >
            <span aria-hidden="true">{icon}</span>{label}
          </NavLink>
        ))}
      </nav>
      <div style={{ marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,.14)', paddingTop: 16 }} />
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F1F5F9', display: 'flex' }}>
      {!isMobile && <aside style={{ width: 248, flexShrink: 0 }}>{sidebar}</aside>}
      {isMobile && mobileOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex' }}>
          <div onClick={() => setMobileOpen(false)} style={{ flex: 1, background: 'rgba(15,23,42,.52)' }} />
          {sidebar}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <header style={{ height: isMobile ? 64 : 76, background: '#fff', borderBottom: '1px solid #D9E2EC', display: 'flex', alignItems: 'center', padding: isMobile ? '0 16px' : '0 32px', gap: 12 }}>
          {isMobile && <button type="button" onClick={() => setMobileOpen(true)} aria-label="Open customer portal menu" style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer' }}>{'\u2630'}</button>}
          {isMobile && <MovdLogo size="small" variant="full" />}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            {!isMobile && <span style={{ color: movdTokens.color.content.secondary, fontSize: 14 }}>Your MOVD move</span>}
            <button type="button" onClick={() => navigate('/')} aria-label="Return to MOVD home" style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' }}>{'\u2039'}</button>
          </div>
        </header>
        <main key={location.pathname}><Outlet /></main>
        <footer style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: isMobile ? '12px 16px' : '12px 32px', background: '#fff', borderTop: '1px solid #D9E2EC' }}>
          <div>
            <p style={{ margin: 0, fontWeight: 800, fontSize: 14 }}>{user.name || user.email || 'MOVD customer'}</p>
            <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontSize: 12 }}>Customer account</p>
          </div>
          <button type="button" onClick={signOut} style={{ background: 'transparent', border: 'none', color: '#475569', fontWeight: 700, cursor: 'pointer' }}>Sign out</button>
        </footer>
      </div>
    </div>
  );
}
